/**
 * Permission Service
 * 
 * Handles fetching and managing user permissions from the backend.
 * Provides methods to check specific permissions and capabilities.
 */

import { managerService } from './manager.service';

class PermissionService {
    constructor() {
        this.permissions = null;
        this.authContext = null;
        this.loading = false;
    }

    /**
     * Fetch user permissions from backend
     * @returns {Promise<Object>}
     */
    async fetchUserPermissions() {
        if (this.loading) {
            return this.permissions;
        }

        this.loading = true;
        
        try {
            const response = await managerService.getUserAuthInfo();
            
            if (response.data && response.data.status === 200 && response.data.result) {
                this.authContext = response.data.result;
                this.permissions = response.data.result.permissions || [];
                
                return this.authContext;
            } else {
                throw new Error('Invalid response format or missing result data');
            }
        } catch (error) {
            console.error('Failed to fetch user permissions:', error);
            // Set safe defaults
            this.authContext = {
                permissions: [],
                roles: ['viewer'],
                canSendPush: false,
                canManageExperiments: false,
                isAdmin: false,
                accessibleApps: []
            };
            this.permissions = [];
            return this.authContext;
        } finally {
            this.loading = false;
        }
    }

    /**
     * Force refresh user permissions (clears cache and refetches)
     * @returns {Promise<Object>}
     */
    async forceRefreshPermissions() {
        // Clear any cached data
        this.permissions = null;
        this.authContext = null;
        this.loading = false;
        
        // Fetch fresh data
        return await this.fetchUserPermissions();
    }

    /**
     * Check if user has a specific permission
     * @param {string} permission - Permission to check
     * @returns {boolean}
     */
    hasPermission(permission) {
        if (!this.permissions) {
            console.warn('Permissions not loaded yet');
            return false;
        }
        
        return this.permissions.includes(permission) || 
               this.permissions.includes('admin.all');
    }

    /**
     * Check if user has any of the specified permissions
     * @param {Array<string>} permissions - Array of permissions to check
     * @returns {boolean}
     */
    hasAnyPermission(permissions) {
        if (!Array.isArray(permissions)) {
            return this.hasPermission(permissions);
        }
        
        return permissions.some(permission => this.hasPermission(permission));
    }

    /**
     * Check if user can edit ongoing experiments
     * @returns {boolean}
     */
    canEditOngoingExperiments() {
        return this.hasPermission('admin.all') || 
               this.hasPermission('experiments.edit_ongoing');
    }

    /**
     * Check if user can edit experiments in general
     * @returns {boolean}
     */
    canEditExperiments() {
        return this.hasPermission('experiments.edit');
    }

    /**
     * Check if user can send push notifications
     * @returns {boolean}
     */
    canSendPushNotifications() {
        return this.authContext?.canSendPush || 
               this.hasPermission('messages.send');
    }

    /**
     * Check if user can schedule push notifications
     * @returns {boolean}
     */
    canSchedulePushNotifications() {
        return this.hasPermission('messages.schedule') ||
               this.hasPermission('messages.send') ||
               this.hasPermission('admin.all');
    }

    /**
     * Check if user can create/save draft messages
     * @returns {boolean}
     */
    canCreateDraftMessages() {
        return this.hasPermission('messages.create') ||
               this.hasPermission('messages.edit');
    }

    /**
     * Check if user can delete experiments
     * @returns {boolean}
     */
    canDeleteExperiments() {
        return this.hasPermission('experiments.delete');
    }

    /**
     * Check if user can start/activate experiments
     * @returns {boolean}
     */
    canActivateExperiments() {
        return this.hasPermission('experiments.activate');
    }

    /**
     * Check if user can view ISL keys
     * @returns {boolean}
     */
    canViewISLKeys() {
        return this.hasPermission('isl_keys.view');
    }

    /**
     * Check if user can create ISL keys
     * @returns {boolean}
     */
    canCreateISLKeys() {
        return this.hasPermission('isl_keys.create');
    }


    canViewExperiments() {
        return this.hasPermission('experiments.view');
    }

    /**
     * Check if user can view retention messages
     * @returns {boolean}
     */
    canViewRetentionMessages() {
        return this.hasPermission('retention.view');
    }

    /**
     * Check if user can create retention messages
     * @returns {boolean}
     */
    canCreateRetentionMessages() {
        return this.hasPermission('retention.create');
    }

    /**
     * Check if user can edit retention messages
     * @returns {boolean}
     */
    canEditRetentionMessages() {
        return this.hasPermission('retention.edit');
    }

    /**
     * Check if user can delete retention messages
     * @returns {boolean}
     */
    canDeleteRetentionMessages() {
        return this.hasPermission('retention.delete');
    }

    /**
     * Check if user can approve retention messages
     * @returns {boolean}
     */
    canApproveRetentionMessages() {
        return this.hasPermission('retention.approve');
    }

    /**
     * Check if user can edit ISL keys
     * @returns {boolean}
     */
    canEditISLKeys() {
        return this.hasPermission('isl_keys.edit');
    }

    /**
     * Check if user can delete ISL keys
     * @returns {boolean}
     */
    canDeleteISLKeys() {
        return this.hasPermission('isl_keys.delete');
    }

    /**
     * Check if user is admin
     * @returns {boolean}
     */
    isAdmin() {
        return this.authContext?.isAdmin || this.hasPermission('admin.all');
    }

    /**
     * Get user's accessible apps
     * @returns {Array|string}
     */
    getAccessibleApps() {
        return this.authContext?.accessibleApps || [];
    }

    /**
     * Get user's roles
     * @returns {Array<string>}
     */
    getUserRoles() {
        return this.authContext?.roles || ['viewer'];
    }

    /**
     * Get complete permission context
     * @returns {Object}
     */
    getPermissionContext() {
        return {
            permissions: this.permissions || [],
            authContext: this.authContext,
            capabilities: {
                canEditExperiments: this.canEditExperiments(),
                canEditOngoingExperiments: this.canEditOngoingExperiments(),
                canSendPushNotifications: this.canSendPushNotifications(),
                canSchedulePushNotifications: this.canSchedulePushNotifications(),
                canCreateDraftMessages: this.canCreateDraftMessages(),
                canDeleteExperiments: this.canDeleteExperiments(),
                canActivateExperiments: this.canActivateExperiments(),
                canViewISLKeys: this.canViewISLKeys(),
                canCreateISLKeys: this.canCreateISLKeys(),
                canEditISLKeys: this.canEditISLKeys(),
                canDeleteISLKeys: this.canDeleteISLKeys(),
                isAdmin: this.isAdmin(),
                canViewExperiments: this.canViewExperiments(),
                canViewRetentionMessages: this.canViewRetentionMessages(),
                canCreateRetentionMessages: this.canCreateRetentionMessages(),
                canEditRetentionMessages: this.canEditRetentionMessages(),
                canDeleteRetentionMessages: this.canDeleteRetentionMessages(),
                canApproveRetentionMessages: this.canApproveRetentionMessages()
            }
        };
    }

    /**
     * Reset permissions (useful for logout)
     */
    resetPermissions() {
        this.permissions = null;
        this.authContext = null;
        this.loading = false;
    }

    /**
     * Get permission explanation for UI tooltips
     * @param {string} action - Action being checked
     * @returns {string}
     */
    getPermissionExplanation(action) {
        const explanations = {
            'edit_ongoing_experiment': 'Only administrators can edit ongoing experiments. Contact an admin or wait until the experiment is paused.',
            'send_push': 'You need push notification permissions to send messages. You can save as draft instead.',
            'schedule_push': 'You need scheduling permissions to schedule messages. You can save as draft instead.',
            'delete_experiment': 'You need delete permissions to remove experiments.',
            'activate_experiment': 'You need activation permissions to start experiments.'
        };
        
        return explanations[action] || 'You do not have permission for this action.';
    }
}

// Export singleton instance
export default new PermissionService(); 